import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import DoubleTranslatedCustomRichText from '@/components/CustomRichText/DoubleTranslatedCustomRichText';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import Spinner from '@/components/UI/Spinner/Spinner';
import * as Constants from '@/helpers/constants';
import { getEditorId, removeParagraphTags, tabLowerCase } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import { setHelpVehicles } from '@/redux/Help/help.action';
import { getHelpVehicles } from '@/redux/Help/help.reducer';
import administrationServices from '@/services/administrationServices';
import { ReduxHelp } from '@/types';
import styles from '../HelpText.module.scss';

interface AffectedVehiclesHelpTextProps {
  helpAffectedVehicles: ReduxHelp;
  setAffectedVehicles: (help: ReduxHelp) => void;
}

const AffectedVehiclesHelpText: React.FC<AffectedVehiclesHelpTextProps> = (props) => {
  const { t } = useTranslation();
  const tab = tabLowerCase(window.location.pathname.split('/')[4]);
  const [originalValues, setOriginalValues] = useState({});
  const { helpAffectedVehicles, setAffectedVehicles } = props;
  const isLoading = helpAffectedVehicles && !helpAffectedVehicles.AFFECTED_VEHICLES_TAB;
  const { addToast } = useToastContext();
  const AFFECTED_VEHICLES_TAB = 'AFFECTED_VEHICLES_TAB';
  const AFFECTED_VEHICLES_SERIES = 'AFFECTED_VEHICLES_SERIES';
  const AFFECTED_VEHICLES_TECHNICAL_ASSESSMENT = 'AFFECTED_VEHICLES_TECHNICAL_ASSESSMENT';

  const ID_GENERAL_AFFECTED_VEHICLES = 'general-affected-vehicles';
  const ID_SERIES_AFFECTED_VEHICLES = 'series-affected-vehicles';
  const ID_VOLUME_AFFECTED_VEHICLES = 'volume-affected-vehicles';
  const ID_TECHNICAL_AFFECTED_VEHICLES = 'technical-affected-vehicles';

  useEffect(() => {
    administrationServices.getHelp(tab)
      .then((data) => {
        const values = helpAffectedVehicles && helpAffectedVehicles.modified ? helpAffectedVehicles : data;
        setAffectedVehicles(values);
        setOriginalValues(values);
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWysiwygChange = (lang, label, data) => {
    const propValues = { ...helpAffectedVehicles[label] };
    const isModified = removeParagraphTags(data) !== removeParagraphTags(originalValues[label][lang]);
    const newState = {
      ...helpAffectedVehicles,
      [label]: {
        ...propValues,
        [lang]: data,
      },
      modified: isModified,
    };

    if (isModified) {
      setAffectedVehicles(newState as unknown as ReduxHelp);
      setOriginalValues(newState);
    }
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <InfoMsg msg={t('administration.helpText.info')} />
          <AccordionPanel title={t('administration.helpText.generalText')}>
            <DoubleTranslatedCustomRichText
              id={ID_GENERAL_AFFECTED_VEHICLES}
              title={t('helptext')}
              initialValue={helpAffectedVehicles?.AFFECTED_VEHICLES_TAB}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, AFFECTED_VEHICLES_TAB, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('affectedVehicles.series')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_SERIES_AFFECTED_VEHICLES}
              title={t('helptext')}
              initialValue={helpAffectedVehicles?.AFFECTED_VEHICLES_SERIES}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, AFFECTED_VEHICLES_SERIES, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('affectedVehicles.volume')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_VOLUME_AFFECTED_VEHICLES}
              title={t('helptext')}
              initialValue={helpAffectedVehicles?.AFFECTED_VEHICLES_SERIES}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, AFFECTED_VEHICLES_SERIES, newValue)}
            />
          </AccordionPanel>
          <AccordionPanel title={`${t('campaignRequests.title')}: ${t('affectedVehicles.technicalAssessmentOfTheTopic')}`}>
            <DoubleTranslatedCustomRichText
              id={ID_TECHNICAL_AFFECTED_VEHICLES}
              title={t('helptext')}
              initialValue={helpAffectedVehicles?.AFFECTED_VEHICLES_TECHNICAL_ASSESSMENT}
              handleInputChange={(lang, newValue) => handleWysiwygChange(lang, AFFECTED_VEHICLES_TECHNICAL_ASSESSMENT, newValue)}
            />
          </AccordionPanel>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  helpAffectedVehicles: getHelpVehicles(state),
});

const actionCreators = {
  setAffectedVehicles: setHelpVehicles,
};

export default connect(mapStateToProps, actionCreators)(AffectedVehiclesHelpText);
